import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseAndValidateParams, getTimeUntilNextHour } from '../utils/urlValidator';
import { validateUrlParams, isCurrentUrlValid, getCurrentUrlParams } from '../utils/urlParamUtils';
import { detectBrowserEnvironment, extractUrlParamsSafely, getSecureBrowserErrorMessage } from '../utils/browserSecurityUtils';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import styled from 'styled-components';
import banpageImg from '../assets/images/banpage.png';
import banpageBtnImg from '../assets/images/banpage-btn.png';
import banpopupImg from '../assets/images/banpopup.png';
import ReceivedMessages from './ReceivedMessages';
import SentMessages from './SentMessages';


// URL 검증 실패 로그 저장 함수 (날짜별 컬렉션)
const logUrlValidationFailure = async (
  url: string, 
  reason: string, 
  timestamp: string, 
  hash?: string,
  browserType?: string
) => {
  try {
    // 현재 날짜를 YYYY-MM-DD 형식으로 생성
    const currentDate = new Date();
    const dateString = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD 형식
    
    const browserInfo = detectBrowserEnvironment();
    
    const logData = {
      url: url,
      reason: reason,
      timestamp: timestamp,
      hash: hash || null,
      failureTime: currentDate,
      userAgent: navigator.userAgent,
      pathname: window.location.pathname,
      date: dateString, // 날짜 필드 추가
      browserType: browserType || browserInfo.browserType, // 브라우저 타입 추가
      isSecureBrowser: browserInfo.isSecureBrowser, // 보안 브라우저 여부
      isPrivateMode: browserInfo.isPrivateMode, // Private 모드 여부
      supportsUrlSearchParams: browserInfo.supportsUrlSearchParams, // URLSearchParams 지원 여부
      supportsLocationSearch: browserInfo.supportsLocationSearch, // location.search 지원 여부
      locationSearch: window.location.search, // 실제 location.search 값
      fullUrl: window.location.href // 전체 URL
    };

    // 날짜별 컬렉션으로 저장 (예: urlValidationFailures_2024-01-15)
    const collectionName = `urlValidationFailures_${dateString}`;
    await addDoc(collection(db, collectionName), logData);
    console.log(`URL 검증 실패 로그가 날짜별 컬렉션(${collectionName})에 저장되었습니다:`, logData);
  } catch (error) {
    console.error('URL 검증 실패 로그 저장 중 오류 발생:', error);
  }
};

// 로딩 및 오류 화면 스타일
const ValidationOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const BanPageImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BanPageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const BanPageButton = styled.button`
  position: absolute;
  top: calc(10% + 95dvw);
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  background: none;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease;
  z-index: 10000;
  
  &:hover {
    transform: translateX(-50%) scale(1.05);
  }
  
  &:active {
    transform: translateX(-50%) scale(0.95);
  }
`;

const BanPageButtonImage = styled.img`
  width: 100%;
  height: auto;
`;

const BanPopupImage = styled.img`
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  z-index: 10002;
  pointer-events: none;
`;


// URL 검증 HOC
export const withUrlValidation = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options: {
    requireValidation?: boolean; // URL 검증이 필요한지 여부
    redirectTo?: string; // 검증 실패 시 리다이렉트할 경로
    showCountdown?: boolean; // 카운트다운을 보여줄지 여부
  } = {}
) => {
  const {
    requireValidation = true,
    redirectTo = '/',
    showCountdown = true
  } = options;

  return (props: P) => {
    const [isValidating, setIsValidating] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [showSentMessages, setShowSentMessages] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
      const validateUrl = async () => {
        
        // URL 검증이 필요하지 않은 경우 바로 통과
        if (!requireValidation) {
          setIsValidating(false);
          setIsValid(true);
          return;
        }

        // 1. 먼저 현재 URL 파라미터 유효성 확인
        const currentUrlValid = await isCurrentUrlValid();
        
        if (currentUrlValid) {
          setIsValidating(false);
          setIsValid(true);
          return;
        }

        // 2. 보안 브라우저 환경에서 안전한 파라미터 추출
        const browserInfo = detectBrowserEnvironment();
        const paramResult = extractUrlParamsSafely();
        const { timestamp, hash, hasParams, extractionMethod } = paramResult;
        
        console.log('브라우저 환경 정보:', browserInfo);
        console.log('파라미터 추출 결과:', paramResult);

        // 3. 파라미터 검증 및 처리
        if (hasParams && timestamp && hash) {
          // 기본적인 파라미터 형식 검증
          if (!timestamp || !hash) {
            await logUrlValidationFailure(
              window.location.href,
              '파라미터 누락 (timestamp 또는 hash가 없음)',
              timestamp || 'null',
              hash || 'null',
              browserInfo.browserType
            );
            setIsValidating(false);
            setIsValid(false);
            return;
          }
          
          // 타임스탬프가 10자리 숫자인지 확인
          if (!/^\d{10}$/.test(timestamp)) {
            await logUrlValidationFailure(
              window.location.href,
              '잘못된 타임스탬프 형식 (10자리 숫자가 아님)',
              timestamp,
              hash,
              browserInfo.browserType
            );
            setIsValidating(false);
            setIsValid(false);
            return;
          }
          
          // 해시가 16자리인지 확인 (기본 검증)
          if (hash.length !== 16) {
            await logUrlValidationFailure(
              window.location.href,
              '잘못된 해시 형식 (16자리가 아님)',
              timestamp,
              hash,
              browserInfo.browserType
            );
            setIsValidating(false);
            setIsValid(false);
            return;
          }
          
          // URLSearchParams 객체 생성
          const searchParams = new URLSearchParams();
          searchParams.set('t', timestamp);
          searchParams.set('h', hash);
          
          const isValidParams = await validateUrlParams(searchParams);
          
          if (isValidParams) {
            setIsValidating(false);
            setIsValid(true);
            return;
          } else {
            // validateUrlParams에서 실패한 경우
            await logUrlValidationFailure(
              window.location.href,
              `URL 검증 실패 (validateUrlParams에서 실패) - 추출방법: ${extractionMethod}`,
              timestamp,
              hash,
              browserInfo.browserType
            );
          }
        } else {
          // URL 파라미터가 없는 경우 - 보안 브라우저 환경 고려
          const errorMessage = browserInfo.isSecureBrowser || browserInfo.isPrivateMode 
            ? getSecureBrowserErrorMessage(browserInfo)
            : 'URL 파라미터 없음 (t 또는 h 파라미터가 없음)';
            
          await logUrlValidationFailure(
            window.location.href,
            errorMessage,
            'null',
            'null',
            browserInfo.browserType
          );
        }

        // 4. 현재 URL 파라미터도 유효하지 않은 경우
        setIsValidating(false);
        setIsValid(false);
      };

      validateUrl();
    }, [location.search, requireValidation, redirectTo, navigate, location.pathname]);

    // 카운트다운 업데이트
    useEffect(() => {
      if (!isValidating && !isValid && showCountdown) {
        const updateCountdown = () => {
          const timeLeft = getTimeUntilNextHour();
          setCountdown(timeLeft);
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 60000); // 1분마다 업데이트

        return () => clearInterval(interval);
      }
    }, [isValidating, isValid, showCountdown]);

    // 검증 중이거나 실패한 경우 오버레이 표시
    if (isValidating || !isValid) {
      return (
        <>
          <ValidationOverlay $visible={true}>
            {isValidating ? (
              // 검증 중일 때는 완전히 빈 화면 (검은 배경만)
              <div style={{ width: '100%', height: '100%', background: '#000000' }} />
            ) : (
              <BanPageContainer>
                <BanPageImage src={banpageImg} alt="접근 제한" />
                <BanPopupImage src={banpopupImg} alt="벤 팝업" />
                <BanPageButton onClick={() => {
                  setShowSentMessages(true);
                }}>
                  <BanPageButtonImage src={banpageBtnImg} alt="보낸 메시지 보기" />
                </BanPageButton>
              </BanPageContainer>
            )}
          </ValidationOverlay>
          {showSentMessages && (
            <>
              <SentMessages onClose={() => {
                setShowSentMessages(false);
              }} />
            </>
          )}
        </>
      );
    }

    // 검증 성공 시 원본 컴포넌트 렌더링
    return (
      <>
        <WrappedComponent {...props} />
        {showSentMessages && (
          <SentMessages onClose={() => setShowSentMessages(false)} />
        )}
      </>
    );
  };
};
